// @flow
import { useEffect, useRef, useState } from 'react';
//Redux related
import type { Connector } from 'react-redux';
import { connect } from 'react-redux';
// Selectors
import { itemSelectorByProductId } from '../selectors';
//Actions
import { changeLineItem } from '../actions';
// Connectors
import { userLoggedInConnector } from '../../user';
// Components
import PriceInformationNotice from '../../common/components/PriceInformationNotice';
import InfoCards from '../../products/components/InfoCards';
import OptionsSelector from '../../products/components/OptionsSelector';
import AddToCartButton from '../containers/AddToCartButton';
import BuyPrice from '../containers/BuyPrice';
import CookingRecommedationBox from './CookingRecommedationBox';
import PortionSlider, { isItemPortioned } from './PortionSlider';
import ProductStorageCondition from './ProductStorageCondition';
import { OptionSwitchCard } from '../../common';
// Common
// Styles
import './BuyBox.css';
// Types
import type { Product } from '../../products/types';
// Data
import { infoCards } from '../../products/data/infoCardsData';
// Assets
import QuestionMarkIcon from '../images/question-mark.svg';
import FatLevelSelector from './FatLevelSelector';
// Hooks
import useDebouncedEffect from '../customHooks/useDebouncedEffect';
// Utils
import { formatRunningOutText, isRunningOut } from '../../products/utils';

export type Props = {
  product: Product,
  item: any,
  handleEdit: (
    itemId: number,
    quantity: Number,
    observation: string,
    portioningSize: Number,
    fatLevel: 'low' | 'medium' | 'high',
    isVacuumSelected: 'vacuum' | null,
  ) => void,
};

const MINIMUM_PORTION_QUANTITY = 500;

const BuyBox = ({ product, item, handleEdit }: Props) => {
  const productOnPromotion =
    parseFloat(product.listPrice) > parseFloat(product.price);

  const [selectedFatLevel, setSelectedFatLevel] = useState('medium');
  const [enableVacuum, setEnableVacuum] = useState(
    product.preselectedVacuumPackage,
  );
  const [eneablePortions, setEneablePortions] = useState(isItemPortioned(item));
  const [portionQuantity, setPortionQuantity] = useState(
    item && item.portioningSize ? item.portioningSize : product.minimumQuantity,
  );

  const [sliderMaxQuantity, setSliderMaxQuantity] = useState(null);

  const pendingPortionsRef = useRef(new Set());
  const previousQuantityRef = useRef(item?.quantity);

  const noItemSliderParameters = {
    quantity: 1000,
    portioningSize: portionQuantity || product.minimumQuantity,
    variant: { unitForQuantity: 'g' },
  };

  useEffect(() => {
    item &&
      handleEdit(
        item.id,
        item.quantity,
        item.observation,
        portionQuantity,
        !item.fatLevel ? 'medium' : selectedFatLevel,
        enableVacuum ? 'vacuum' : null,
      );
  }, [selectedFatLevel]);

  useEffect(() => {
    item &&
      handleEdit(
        item.id,
        item.quantity,
        item.observation,
        portionQuantity,
        item.fatLevel,
        enableVacuum ? 'vacuum' : null,
      );
  }, [enableVacuum]);

  useEffect(() => {
    if (!item) {
      setSliderMaxQuantity(product.defaultQuantity);
      setPortionQuantity(product.defaultQuantity);
    }

    if (item) {
      const quantity = !eneablePortions
        ? item.quantity
        : (item.portioningSize ?? item.quantity);

      setPortionQuantity(quantity);

      handleEdit(
        item.id,
        item.quantity,
        item.observation,
        portionQuantity,
        item.fatLevel,
        enableVacuum ? 'vacuum' : null,
      );
    }
  }, [eneablePortions]);

  useDebouncedEffect(
    signal => {
      if (!item || signal.aborted) return;

      const quantityToUse = portionQuantity || item.quantity;

      if (
        portionQuantity >= MINIMUM_PORTION_QUANTITY &&
        portionQuantity <= item.quantity
      ) {
        pendingPortionsRef.current.add(quantityToUse);
        handleEdit(
          item.id,
          item.quantity,
          item.observation,
          quantityToUse,
          item.fatLevel,
          enableVacuum ? 'vacuum' : null,
        );
      }
    },
    [portionQuantity],
    400,
  );

  useEffect(() => {
    if (item) {
      setSelectedFatLevel(!item.fatLevel ? selectedFatLevel : item.fatLevel);
      setEnableVacuum(item.packagingName ? true : enableVacuum);
      setEneablePortions(eneablePortions || isItemPortioned(item));
      setPortionQuantity(
        item.portioningSize
          ? item.portioningSize
          : eneablePortions
            ? portionQuantity
            : item.quantity,
      );
      setSliderMaxQuantity(item.quantity);
      handleEdit(
        item.id,
        item.quantity,
        item.observation,
        portionQuantity,
        selectedFatLevel,
        enableVacuum ? 'vacuum' : null,
      );
    }
    if (!item) {
      setPortionQuantity(null);
      setEneablePortions(false);
    }
  }, [item?.id]);

  useEffect(() => {
    if (
      item &&
      item.portioningSize &&
      item.portioningSize !== portionQuantity
    ) {
      if (pendingPortionsRef.current.has(item.portioningSize)) {
        pendingPortionsRef.current.delete(item.portioningSize);
      } else {
        setPortionQuantity(item.portioningSize);
      }
    }
  }, [item?.portioningSize]);

  // Quantity changes made by other buttons (ex: cart preview) don't go through this page's button handlers
  useEffect(() => {
    const previousQuantity = previousQuantityRef.current;
    previousQuantityRef.current = item?.quantity;

    if (
      !item ||
      previousQuantity == null ||
      previousQuantity === item.quantity
    ) {
      return;
    }

    setSliderMaxQuantity(item.quantity);
    setPortionQuantity(portion => {
      if (portion == null) return portion;

      return portion === previousQuantity
        ? item.quantity
        : Math.min(portion, item.quantity);
    });
  }, [item?.quantity]);

  return (
    <div className="buy-box-main">
      <div className="buy-box-title">
        <p className="buy-box-manufacturer">{product.manufacturer}</p>
        <h1 className="buy-box-product-name">{product.name}</h1>
        <ProductStorageCondition storageCondition={product.storageCondition} />
      </div>
      <section className="buy-box-section">
        <CookingRecommedationBox
          cookingMethods={product.cookingRecommendations}
        />
        <OptionsSelector
          options={product.options}
          productPrice={product.price}
          productUnit={product.unitForQuantity}
        />

        {product.fatLevelEnabled && (
          <FatLevelSelector
            fatLevel={selectedFatLevel}
            updateFatLevel={setSelectedFatLevel}
          />
        )}
        <>
          {product.unitForQuantity === 'g' && (
            <>
              <div className="option-switch-container">
                {product.vacuumPackable && (
                  <OptionSwitchCard
                    title="Embalagem a Vácuo"
                    description="Mais frescor, sabor e durabilidade para a carne."
                    badgeText="Opcional"
                    value={enableVacuum}
                    onChange={setEnableVacuum}
                  />
                )}

                <OptionSwitchCard
                  title="Porcionar Produto"
                  description="Tenha seu produto separado do seu jeito."
                  badgeText="Opcional"
                  value={eneablePortions}
                  onChange={setEneablePortions}
                />
              </div>

              {eneablePortions && (
                <PortionSlider
                  portion={
                    portionQuantity ? portionQuantity : product.minimumQuantity
                  }
                  item={item ? item : noItemSliderParameters}
                  onCommit={setPortionQuantity}
                  itemQuantity={sliderMaxQuantity}
                />
              )}
            </>
          )}
        </>

        <div className="buy-box-section-price-container">
          <div className="buy-box-section-price-container-action">
            <AddToCartButton
              showNotifyMeButton={true}
              productId={product.id}
              slug={product.slug}
              unitForQuantity={product.unitForQuantity}
              defaultQuantity={product.defaultQuantity}
              incrementQuantity={product.incrementQuantity}
              inStock={product.inStock}
              discontinued={product.discontinued}
              shouldNotifyUser={product.shouldNotifyUser}
              analyticsEventType={'visit'} // USE ANALYTICS
              eneableFatLevel={product.fatLevelEnabled}
              eneablePackagingName={product.vacuumPackable}
              fatLevel={item && item.fatLevel}
              packagingName={item && item.packagingName}
              portionQuantity={portionQuantity}
              setPortionQuantity={setPortionQuantity}
              setSliderMaxQuantity={setSliderMaxQuantity}
            />

            {product.unitForQuantity !== 'un' && (
              <p className="buy-box-min-quantity">
                Quantidade mínima: &nbsp;
                {product.unitForQuantity === 'g'
                  ? (product.minimumQuantity / 1000).toLocaleString('pt-br', {
                      currency: 'BRL',
                    }) + ' Kg'
                  : product.minimumQuantity}
              </p>
            )}

            {isRunningOut(product) && (
              <span className="buy-box-running-out">
                Apenas {formatRunningOutText(product)} em estoque!
              </span>
            )}
          </div>

          <div className="buy-box-sub-container">
            <p className="buy-box-sub-container-title">Preço Estimado</p>
            <div className="buy-box-sub-container-wrapper">
              <img
                src={QuestionMarkIcon}
                alt="entenda o preco"
                className="buy-box-question-mark"
              />
              <div className="buy-box-understand-the-price">
                Entenda o preço
              </div>
              <div className="buy-box-price-content">
                <PriceInformationNotice />
              </div>
            </div>

            <BuyPrice
              productId={product.id}
              listPrice={product.listPrice}
              price={product.price}
              unit={product.unitForQuantity}
              showPromo={productOnPromotion}
            />
          </div>
        </div>
        <InfoCards
          cards={infoCards}
          classNames={{
            container: 'buy-box-info-container',
            image: 'buy-box-info-icon',
          }}
        />
      </section>
    </div>
  );
};

const mapStateToProps = (state, props: Props) => {
  const item = itemSelectorByProductId(state, props.product.id);
  return {
    item: item,
  };
};

const mapDispatchToProps = (dispatch: Dispatch, ownProps: OwnProps) => {
  return {
    handleEdit: (
      itemId: number,
      quantity: Number,
      observation: string,
      portioningSize: number,
      fatLevel: 'low' | 'medium' | 'high',
      packagingName: 'vacuum' | null,
    ) => {
      dispatch(
        changeLineItem(
          itemId,
          observation,
          quantity,
          portioningSize,
          fatLevel,
          packagingName,
        ),
      );
    },
  };
};

const connector: Connector<{}, Props> = connect(
  mapStateToProps,
  mapDispatchToProps,
);

export default userLoggedInConnector(connector(BuyBox));
